import { t } from "@lingui/core/macro";

export enum Permissions {
  ANALYTICS = "analytics",
  ASSETS = "assets",
  COMPANY_ADMIN = "company.admin",

  // To access the manage section global.admin permission is required
  MANAGE = "global.admin",
}

export type Pagination = {
  total: number;
  totalPages: number;
  page: number;
  pageSize: number;
};

export type ApiResponse<T> = {
  data: T;
};

export type ApiResponsePaginated<T> = {
  data: T;
  meta: {
    pagination: Pagination;
  };
};

export type GetAllParams = {
  page?: number;
  pageSize?: number;
  search?: string;
  sort?: string;
  order?: string;
  nested?: string;
};

export type QueryOptions = {
  enabled?: boolean;
  refetchInterval?: number;
  staleTime?: number;
};

export const pageSizes = [10, 20, 50];
export const defaultPageSize = 10;
export const invalidCharacters = /^[^<>&"'\\/:*?|]*$/;
export const commonDebounceDelay = 400;
// Presumably point groups won't have more than 30 - 40 points - so we are fetching them all in a single query
// Set maximum amount of points to be fetched per point group query
export const maxPointGroupPoints = 500;

export function defaultTableTexts() {
  return {
    activeFiltersText: t`Active filters:`,
    clearAllFiltersButtonText: t`CLEAR`,
    emptyResultsText: t`No results found`,
    filtersSelectedText: t`selected`,
    searchPlaceholderText: t`Search`,
  };
}

export function getMonthTranslation(month: string): string {
  const monthTranslationMap = {
    Jan: t`Jan`,
    Feb: t`Feb`,
    Mar: t`Mar`,
    Apr: t`Apr`,
    May: t`May`,
    Jun: t`Jun`,
    Jul: t`Jul`,
    Aug: t`Aug`,
    Sep: t`Sep`,
    Oct: t`Oct`,
    Nov: t`Nov`,
    Dec: t`Dec`,
  };
  return monthTranslationMap[month];
}

export enum DataType {
  Heating = 1,
  Electricity,
  Cooling,
  ColdWater,
  HotWater,
}

export enum DataUnit {
  kWh = 1,
  Mwh,
  M3,
  L,
}

export const getDataType = () => [
  {
    id: null,
    title: t`Empty`,
    desc: t`Current values will be cleared`,
  },
  {
    id: DataType.Heating,
    title: t`Heating`,
  },
  {
    id: DataType.Electricity,
    title: t`Electricity`,
  },
  {
    id: DataType.ColdWater,
    title: t`Cold water`,
  },
  {
    id: DataType.HotWater,
    title: t`Hot water`,
  },
  {
    id: DataType.Cooling,
    title: t`Cooling`,
  },
];

export const getDataUnits = () => [
  {
    id: null,
    title: t`Empty`,
    desc: t`Current values will be cleared`,
  },
  {
    id: DataUnit.kWh,
    title: "kWh",
  },
  {
    id: DataUnit.Mwh,
    title: "MWh",
  },
  {
    id: DataUnit.M3,
    title: "m3",
  },
  {
    id: DataUnit.L,
    title: "l",
  },
];

export const DATA_TYPE_UNIT_MAP: Record<DataType, DataUnit[]> = {
  [DataType.Electricity]: [DataUnit.kWh, DataUnit.Mwh],
  [DataType.Heating]: [DataUnit.kWh, DataUnit.Mwh],
  [DataType.Cooling]: [DataUnit.kWh, DataUnit.Mwh],
  [DataType.ColdWater]: [DataUnit.M3, DataUnit.L],
  [DataType.HotWater]: [DataUnit.M3, DataUnit.L],
};
